import { Input, Label, TextField } from "@/components/ui/field";
import { Heading } from "@/components/ui/heading";
import { NumberField, NumberInput } from "@/components/ui/number-field";
import {
  Select,
  SelectItem,
  SelectList,
  SelectTrigger,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import type { LabelField, LabelSettings } from "../types";
import { calculateBarcodeWidthForDPI } from "../utils/barcodeUtils";
import { calculateTextDimensions } from "../utils/textUtils";

interface BarcodeSettingsProps {
  selectedField: LabelField | null;
  onUpdateField: (id: string, updates: Partial<LabelField>) => void;
  settings: LabelSettings;
}

// Only Code 128 is supported for now
// Future: Add DataMatrix, QR Code, etc.

const FONT_FAMILIES = [
  { id: "0", name: "Default" },
  { id: "A", name: "Font A" },
  { id: "B", name: "Font B" },
  { id: "C", name: "Font C" },
  { id: "D", name: "Font D" },
  { id: "E", name: "Font E" },
  { id: "F", name: "Font F" },
  { id: "G", name: "Font G" },
  { id: "H", name: "Font H" },
];

const ALIGNMENTS = [
  { id: "L", name: "Left" },
  { id: "C", name: "Center" },
  { id: "R", name: "Right" },
];

export function BarcodeSettings({
  selectedField,
  onUpdateField,
  settings,
}: BarcodeSettingsProps) {
  if (!selectedField) {
    return (
      <div className="space-y-4">
        <Heading level={3}>Field Properties</Heading>
        <p className="text-sm text-muted-foreground">
          Select a field to edit its properties
        </p>
      </div>
    );
  }

  const handleUpdate = (updates: Partial<LabelField>) => {
    onUpdateField(selectedField.id, updates);
  };

  return (
    <div className="space-y-4">
      <Heading level={3}>Field Properties</Heading>

      <TextField
        value={selectedField.content}
        onChange={(value) => {
          const updates: Partial<LabelField> = { content: value };

          // Recalculate dimensions for Code 128 barcodes
          if (selectedField.type === "barcode") {
            const calculation = calculateBarcodeWidthForDPI(
              value,
              settings.dpi,
              0.0075, // min X dimension
              selectedField.barcodeHeight || 50,
              selectedField.showText ?? true,
              12, // font size
            );
            updates.width = calculation.widthInDots;
            updates.height = calculation.heightInDots;
          } else if (selectedField.type === "text") {
            // Recalculate height for text fields
            const textCalc = calculateTextDimensions(
              value,
              selectedField.fontSize || 20,
              selectedField.fontFamily || "0",
              selectedField.width,
            );
            updates.height = textCalc.heightInDots;
          }

          handleUpdate(updates);
        }}
      >
        <Label>Content</Label>
        <Input />
      </TextField>

      <div className="grid grid-cols-2 gap-2">
        <NumberField
          value={selectedField.x}
          onChange={(value) => handleUpdate({ x: value })}
          minValue={0}
        >
          <Label>X Position</Label>
          <NumberInput />
        </NumberField>
        <NumberField
          value={selectedField.y}
          onChange={(value) => handleUpdate({ y: value })}
          minValue={0}
        >
          <Label>Y Position</Label>
          <NumberInput />
        </NumberField>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <NumberField
          value={selectedField.width}
          onChange={(value) => handleUpdate({ width: value })}
          minValue={10}
        >
          <Label>Width</Label>
          <NumberInput />
        </NumberField>
        <NumberField
          value={selectedField.height}
          onChange={(value) => handleUpdate({ height: value })}
          minValue={10}
        >
          <Label>Height</Label>
          <NumberInput />
        </NumberField>
      </div>

      {selectedField.type === "text" && (
        <>
          <NumberField
            value={selectedField.fontSize || 20}
            onChange={(value) => {
              const updates: Partial<LabelField> = { fontSize: value };

              // Recalculate height for text fields
              const textCalc = calculateTextDimensions(
                selectedField.content,
                value,
                selectedField.fontFamily || "0",
                selectedField.width,
              );
              updates.height = textCalc.heightInDots;

              handleUpdate(updates);
            }}
            minValue={8}
            maxValue={100}
          >
            <Label>Font Size</Label>
            <NumberInput />
          </NumberField>

          <Select
            selectedKey={selectedField.fontFamily || "0"}
            onSelectionChange={(key) => {
              const updates: Partial<LabelField> = {
                fontFamily: key as string,
              };

              // Recalculate height for text fields
              const textCalc = calculateTextDimensions(
                selectedField.content,
                selectedField.fontSize || 20,
                key as string,
                selectedField.width,
              );
              updates.height = textCalc.heightInDots;

              handleUpdate(updates);
            }}
          >
            <Label>Font Family</Label>
            <SelectTrigger />
            <SelectList>
              {FONT_FAMILIES.map((font) => (
                <SelectItem key={font.id} id={font.id}>
                  {font.name}
                </SelectItem>
              ))}
            </SelectList>
          </Select>

          <Select
            selectedKey={selectedField.alignment || "L"}
            onSelectionChange={(key) =>
              handleUpdate({ alignment: key as "L" | "C" | "R" | "J" })
            }
          >
            <Label>Alignment</Label>
            <SelectTrigger />
            <SelectList>
              {ALIGNMENTS.map((align) => (
                <SelectItem key={align.id} id={align.id}>
                  {align.name}
                </SelectItem>
              ))}
            </SelectList>
          </Select>

          <Switch
            isSelected={selectedField.truncate ?? false}
            onChange={(isSelected) => handleUpdate({ truncate: isSelected })}
          >
            Truncate long text instead of wrapping
          </Switch>
        </>
      )}

      {selectedField.type === "barcode" && (
        <>
          {/* Barcode type is fixed to Code 128 for now */}
          <div className="text-sm text-muted-foreground">
            <Label>Barcode Type</Label>
            <div className="mt-1 px-3 py-2 bg-gray-50 rounded border">
              Code 128
            </div>
          </div>

          <NumberField
            value={selectedField.barcodeHeight || 50}
            onChange={(value) => {
              const updates: Partial<LabelField> = { barcodeHeight: value };

              // Recalculate total height for Code 128 barcodes
              const calculation = calculateBarcodeWidthForDPI(
                selectedField.content,
                settings.dpi,
                0.0075, // min X dimension
                value,
                selectedField.showText ?? true,
                12, // font size
              );
              updates.height = calculation.heightInDots;

              handleUpdate(updates);
            }}
            minValue={20}
            maxValue={200}
          >
            <Label>Barcode Height</Label>
            <NumberInput />
          </NumberField>

          <Switch
            isSelected={selectedField.showText ?? true}
            onChange={(isSelected) => {
              const updates: Partial<LabelField> = { showText: isSelected };

              // Recalculate total height for Code 128 barcodes
              const calculation = calculateBarcodeWidthForDPI(
                selectedField.content,
                settings.dpi,
                0.0075, // min X dimension
                selectedField.barcodeHeight || 50,
                isSelected,
                12, // font size
              );
              updates.height = calculation.heightInDots;

              handleUpdate(updates);
            }}
          >
            Show Text Below Barcode
          </Switch>
        </>
      )}
    </div>
  );
}
